import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Modal, ModalBody, ModalFooter } from "reactstrap";
import i18next from "i18next";
import { BsDownload } from "react-icons/bs";
import { requestFileUploadUrl } from "actions/barcodeQtyImportActions.js";
import { currentUserDataSelector } from "selectors/currentUserSelector";
import { buyplanIdSelector, buyplanStateSelector } from "selectors/buyplanIdSelector.js";
import { bannerInfoDataSelector } from "selectors/bannerInfoSelector.js";
import { releaseMultipleMonthsSelector } from "selectors/buyplanInfoSelector.js"
import { stockModelIdSelector } from "selectors/stockModelIdSelector";
import { warehouseIdSelector } from "selectors/warehouseSelector.js";
import { fileUploadStatusSelector } from "selectors/barcodeQtyImportSelector.js"
import { allowBarcodeQtyImportSelector } from "selectors/filtersSelector.js";
import BarcodeFileUploadForm from "./form.jsx"
import { shouldUserUpdateQty } from "utils.js";
import { addNotification } from "notificationStore";

const BarcodeQtyImport = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(currentUserDataSelector);
  const buyplanState = useSelector(buyplanStateSelector);
  const bannerInfoData = useSelector(bannerInfoDataSelector);
  const releaseStatus = bannerInfoData.get("release_status");
  const allowBarcodeQtyImport = useSelector(allowBarcodeQtyImportSelector);
  const confirmedBuyplanId = useSelector(buyplanIdSelector);
  const confirmedStockModalId = useSelector(stockModelIdSelector);
  const warehouseId = useSelector(warehouseIdSelector);
  const isFileUploadedSuccesfully = useSelector(fileUploadStatusSelector)
  const isMultipleMonthsForRelease = useSelector(releaseMultipleMonthsSelector)
  const defaultFormData = {
    file: null,
    barcode: '',
    quantity: '',
    delivery_month: ''
  }

  const [isModalOpen, setModalOpen] = useState(false);
  const [fileHeaders, setFileHeaders] = useState([]);
  const [formData, setFormData] = useState(defaultFormData);
  const [buttonText, setButtonText] = useState("Upload");

  useEffect(() => {
    closeModal()
    setButtonText("Upload")
    setFormData(defaultFormData)
  }, [isFileUploadedSuccesfully]);

  const openModal = () => {
    setModalOpen(true);
  }
  const closeModal = () => {
    setModalOpen(false);
    setButtonText("Upload")
    setFormData(defaultFormData)
  };

  const isFormValid = () => {
    return formData.file !== null && formData.barcode.trim() !== '' && formData.quantity.trim() !== '';
  };

  const handleSubmit = (evt) => {
    evt.preventDefault();
    setButtonText("Uploading...");
    const fileExtension = formData.file.name.split('.').pop().toLowerCase(); 
    if (!["csv", "xlsx", "xls"].includes(fileExtension)) {
      addNotification({ type: "danger", message: i18next.t("barcode.invalidFileFormat") });
      return;
    }
    else {
      dispatch(
        requestFileUploadUrl({
          file: formData.file,
          file_name: formData.file.name,
          barcode: formData.barcode,
          quantity: formData.quantity,
          delivery_month: formData.delivery_month,
          buyplan_id: confirmedBuyplanId,
          stock_model_id: confirmedStockModalId,
          warehouse_id: warehouseId,
          headers: fileHeaders
        })
      );
      setFormData({
        ...formData,
        barcode: '',
        quantity: '',
        delivery_month: '',
        file: null
      });
    }
  };

  const shouldCurrentUserUpdateQty = shouldUserUpdateQty( confirmedBuyplanId,
                                                          confirmedStockModalId,
                                                          currentUser.get("role"),
                                                          buyplanState,
                                                          releaseStatus)

  const shouldMapDeliveryMonths = confirmedBuyplanId && isMultipleMonthsForRelease

  return (
    <>
      { allowBarcodeQtyImport && shouldCurrentUserUpdateQty &&
        <Button
          className="w-75"
          size="sm"
          style={{ marginLeft: "20px", marginBottom: "10px" }}
          color="primary"
          onClick={openModal}
          title="Import Qty"
        >
          <BsDownload /> Import Qty
        </Button>
      }

      {isModalOpen && (
        <Modal
          className={"modal-container mw-100 w-75"}
          isOpen={isModalOpen}
          toggle={closeModal}
          centered
          autoFocus={false}
          backdrop="static"
        >
          <ModalBody className="w-100">
            <div className="d-flex justify-content-between mt-2">
              <div className="d-flex">  
                <h6>Barcode Qty Import</h6>
              </div>

              <div className="d-flex justify-content-between">
                <Button type="submit" color="primary" onClick={handleSubmit} disabled={!isFormValid()}>
                  {buttonText}
                </Button>
                <Button onClick={closeModal} className="ml-2" color="primary">
                  Exit
                </Button>
              </div>
            </div>
            <hr />
            <BarcodeFileUploadForm
              fileHeaders={fileHeaders}
              setFileHeaders={setFileHeaders}
              formData={formData}
              setFormData={setFormData}
              shouldMapDeliveryMonths={shouldMapDeliveryMonths}
              />
            
          </ModalBody>
          <ModalFooter className="mt-4"></ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default React.memo(BarcodeQtyImport);
